import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Award, Microscope, Droplets, ShieldCheck } from "lucide-react";
import KineticHeading from "@/components/KineticHeading";
import Reveal from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import SEO from "@/components/SEO";
import { LAB } from "@/data/lab";
import { testCategories } from "@/data/tests";

const HERO_SLIDES = [
  { src: LAB.images.front, alt: "Lifeline Diagnostics — Jaysingpur front", label: "The Facade" },
  { src: LAB.images.interior, alt: "Inside Lifeline Diagnostics — lab equipment", label: "Inside the lab" },
];

function HeroImage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "12%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.18]);

  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(id);
  }, []);

  const current = HERO_SLIDES[index];

  return (
    <div
      ref={ref}
      className="relative w-full h-[62vh] md:h-[78vh] overflow-hidden bg-navy"
      data-testid="hero-image"
    >
      <AnimatePresence mode="sync">
        <motion.img
          key={index}
          src={current.src}
          alt={current.alt}
          initial={{ opacity: 0, scale: 1.12 }}
          animate={{ opacity: 1, scale: 1.08 }}
          exit={{ opacity: 0, scale: 1.04 }}
          transition={{ duration: 1.6, ease: [0.7, 0, 0.2, 1] }}
          style={{ y }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>

      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/85" />
      <div className="absolute inset-0 spotlight mix-blend-multiply opacity-70" />

      {/* Slide dots + caption */}
      <div className="absolute bottom-6 md:bottom-10 left-6 md:left-10 right-6 md:right-10 flex items-end justify-between text-white pointer-events-none">
        <motion.span
          key={`cap-${index}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.7, 0, 0.2, 1] }}
          className="font-display italic text-2xl md:text-4xl drop-shadow-lg"
          data-testid="hero-slide-caption"
        >
          {current.label}
        </motion.span>
        <div className="flex items-center gap-3 pointer-events-auto">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              data-testid={`hero-slide-dot-${i}`}
              aria-label={`Show slide ${i + 1}`}
              className={`h-[3px] transition-all duration-500 ${
                i === index ? "w-14 bg-white" : "w-8 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <div data-testid="page-home">
      <SEO
        title="Lifeline Diagnostics — ISO Certified Diagnostic Lab in Jaysingpur | Dr. Neelam Attar"
        description="Lifeline Diagnostics is an ISO-certified diagnostic laboratory in Jaysingpur run by Dr. Neelam Attar (MD Microbiology) since 2010. Microbiology, biochemistry, pathology, hematology, thyroid, dengue, CBC, HbA1c and hospital water & infection-control testing."
        keywords="Lifeline Diagnostics, diagnostic lab Jaysingpur, pathology lab Jaysingpur, blood test Jaysingpur, Dr Neelam Attar, ISO certified lab Kolhapur, CBC, thyroid test, HbA1c, dengue test, culture and sensitivity, microbiology lab"
        path="/"
      />
      {/* HERO */}
      <section className="relative pt-32 md:pt-36 pb-16 md:pb-24 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {/* Logo above the heading — sized to fill the blank space */}
          <motion.img
            src={LAB.images.logo}
            alt="Lifeline Diagnostics"
            data-testid="hero-logo-mark"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
            className="w-full max-w-[900px] mx-auto h-auto object-contain mb-16"
          />

          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 md:col-span-8">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="flex items-center gap-3 mb-8 text-navy"
              >
                <span className="h-px w-10 bg-navy" />
                <span className="font-mono-tag">
                  Est. {LAB.established} · Jaysingpur, MH
                </span>
              </motion.div>

              <KineticHeading
                lines={["Precision", "pathology,", <em key="e" className="italic font-medium text-cyan-brand">a steady hand.</em>]}
                className="font-display text-[16vw] md:text-[9vw] lg:text-[8.4vw] leading-[0.9] tracking-[-0.03em] text-navy"
              />
            </div>

            <div className="col-span-12 md:col-span-4 md:pl-6">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.9, ease: [0.7, 0, 0.2, 1] }}
                className="text-slate-600 leading-relaxed text-base md:text-lg max-w-sm"
              >
                An ISO-certified diagnostic laboratory run by
                <span className="text-navy"> Dr. Neelam Attar (MD Microbiology) </span>
                — serving clinicians, hospitals and the people of
                Jaysingpur with quality-controlled results since 2010.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.05, ease: [0.7, 0, 0.2, 1] }}
                className="mt-8 flex flex-wrap items-center gap-4"
              >
                <Link
                  to="/tests"
                  data-testid="hero-cta-tests"
                  className="group inline-flex items-center gap-3 bg-navy text-white px-5 py-3 text-[13px] font-mono-tag hover:bg-cyan-brand transition-colors duration-500"
                >
                  Explore Tests
                  <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-500" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="hero-cta-contact"
                  className="group inline-flex items-center gap-3 text-navy border-b border-navy pb-1 text-[13px] font-mono-tag"
                >
                  Visit the lab
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-500" />
                </Link>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Meta strip */}
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-20">
          <div className="grid grid-cols-3 gap-y-8 border-t hair pt-8">
            {[
              { k: "15+", v: "Years of service" },
              { k: "12", v: "Test categories" },
              { k: "ISO", v: "Certified lab" },
            ].map((m, i) => (
              <Reveal key={i} delay={i * 0.08} className="flex flex-col">
                <span className="font-display text-4xl md:text-5xl text-navy tracking-tight">
                  {m.k}
                </span>
                <span className="font-mono-tag text-slate-500 mt-2">{m.v}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PARALLAX HERO IMAGE */}
      <HeroImage />

      {/* MARQUEE */}
      <EditorialMarquee />

      {/* ABOUT TEASER */}
      <section className="py-24 md:py-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-8">
            <Reveal className="col-span-12 md:col-span-4">
              <p className="font-mono-tag text-cyan-brand">— About the Lab</p>
              <div className="mt-4 font-mono-tag text-slate-500">01 / 04</div>
            </Reveal>
            <div className="col-span-12 md:col-span-8">
              <Reveal>
                <h2 className="font-display text-4xl md:text-6xl leading-[1] tracking-tight text-navy">
                  A quiet lab in Jaysingpur where
                  <em className="italic text-cyan-brand"> accuracy </em>
                  is a daily discipline.
                </h2>
              </Reveal>
              <Reveal delay={0.15} className="mt-10 grid md:grid-cols-2 gap-10 text-slate-700 leading-relaxed">
                <p>
                  Lifeline Diagnostics is an ISO-certified laboratory thriving
                  under the proprietorship of Dr. Neelam Attar (MBBS, MD
                  Microbiology) in Jaysingpur since 2010. The lab is a spacious,
                  aesthetically calm set-up with three qualified technicians
                  and support staff.
                </p>
                <p>
                  Daily quality controls and yearly machine calibrations sit
                  quietly behind every report we sign — because a diagnosis
                  is only useful when it is right.
                </p>
              </Reveal>
              <Reveal delay={0.3} className="mt-10">
                <Link
                  to="/about"
                  data-testid="home-about-link"
                  className="group inline-flex items-center gap-3 text-navy border-b border-navy pb-1 text-[13px] font-mono-tag"
                >
                  Read the full story
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-500" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITY GRID (bento-ish) */}
      <section className="pb-24 md:pb-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="font-mono-tag text-cyan-brand">— What we do</p>
          </Reveal>
          <Reveal delay={0.1} className="mt-4 flex items-end justify-between flex-wrap gap-4">
            <h3 className="font-display text-4xl md:text-6xl leading-[1] tracking-tight text-navy max-w-3xl">
              Four disciplines. <em className="italic">One promise.</em>
            </h3>
            <div className="font-mono-tag text-slate-500">02 / 04</div>
          </Reveal>

          <div className="mt-16 grid grid-cols-12 gap-4 md:gap-6">
            {[
              {
                icon: Microscope,
                title: "Microbiology",
                text: "Cultures, stains, sensitivities and hospital surveillance under Dr. Attar's direct oversight.",
                span: "md:col-span-7",
                dark: true,
              },
              {
                icon: Droplets,
                title: "Water & Environmental",
                text: "Water, air-settle and disinfectant efficiency testing for hospitals and industries.",
                span: "md:col-span-5",
              },
              {
                icon: Award,
                title: "Biochemistry & Pathology",
                text: "Everyday clinical chemistry — thyroid, diabetes, kidney, liver, lipid — with quality controls.",
                span: "md:col-span-5",
              },
              {
                icon: ShieldCheck,
                title: "Infection Control Guidance",
                text: "Training on antimicrobial stewardship and infection control aligned with NABH requirements.",
                span: "md:col-span-7",
                dark: true,
              },
            ].map((c, i) => (
              <Reveal
                key={i}
                delay={i * 0.08}
                className={`col-span-12 ${c.span} p-8 md:p-10 min-h-[280px] flex flex-col justify-between ${
                  c.dark
                    ? "bg-navy text-white grain"
                    : "bg-paper text-navy border hair"
                }`}
              >
                <c.icon
                  className={`h-8 w-8 ${
                    c.dark ? "text-cyan-brand" : "text-cyan-brand"
                  }`}
                  strokeWidth={1.5}
                />
                <div className="mt-8">
                  <h4 className="font-display text-3xl md:text-4xl leading-tight tracking-tight">
                    {c.title}
                  </h4>
                  <p
                    className={`mt-4 max-w-md leading-relaxed ${
                      c.dark ? "text-white/70" : "text-slate-600"
                    }`}
                  >
                    {c.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTS PREVIEW */}
      <section className="pb-24 md:pb-40 bg-paper">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 md:pt-40">
          <Reveal>
            <p className="font-mono-tag text-cyan-brand">— The Test Directory</p>
          </Reveal>
          <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
            <Reveal delay={0.1}>
              <h3 className="font-display text-4xl md:text-6xl leading-[1] tracking-tight text-navy max-w-3xl">
                <em className="italic">Twelve</em> disciplines,
                <br /> one address.
              </h3>
            </Reveal>
            <Reveal delay={0.15} className="font-mono-tag text-slate-500">
              03 / 04
            </Reveal>
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-4">
            {testCategories.map((c, i) => (
              <Reveal
                key={c.id}
                delay={i * 0.04}
                className="flex items-baseline gap-3 border-b hair py-4"
              >
                <span className="font-mono-tag text-slate-400">{c.number}</span>
                <span className="font-display text-lg md:text-xl text-navy">
                  {c.title}
                </span>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3} className="mt-14">
            <Link
              to="/tests"
              data-testid="home-tests-link"
              className="group inline-flex items-center gap-3 bg-navy text-white px-5 py-3 text-[13px] font-mono-tag hover:bg-cyan-brand transition-colors duration-500"
            >
              Open full test directory
              <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-500" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* MAP */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
            <Reveal>
              <p className="font-mono-tag text-cyan-brand">— On the map</p>
              <h3 className="mt-3 font-display text-3xl md:text-5xl leading-[1] tracking-tight text-navy">
                Rising Sun Apartment, <em className="italic">Jaysingpur</em>
              </h3>
            </Reveal>
            <Reveal delay={0.1}>
              <a
                href={LAB.mapsUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="home-open-maps"
                className="inline-flex items-center gap-3 bg-navy text-white px-5 py-3 text-[13px] font-mono-tag hover:bg-cyan-brand transition-colors duration-500"
              >
                Open in Google Maps
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </Reveal>
          </div>

          <Reveal delay={0.2} className="border hair overflow-hidden">
            <iframe
              title="Lifeline Diagnostics map"
              src={LAB.mapsEmbed}
              className="w-full h-[60vh] block"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="home-map-embed"
            />
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-40 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-8 items-end">
            <Reveal className="col-span-12 md:col-span-8">
              <p className="font-mono-tag text-cyan-brand">— Visit</p>
              <h3 className="mt-4 font-display text-4xl md:text-6xl leading-[1] tracking-tight text-navy">
                Have a test in hand? <em className="italic">Just call.</em>
              </h3>
              <p className="mt-8 max-w-xl text-slate-600 leading-relaxed">
                No appointments needed. Walk in during working hours or ring
                the lab and Dr. Attar's team will guide you through
                collection, timelines and reporting.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="col-span-12 md:col-span-4 md:pl-8">
              <div className="font-mono-tag text-slate-500">04 / 04</div>
              <a
                href={LAB.phoneHref}
                data-testid="home-cta-phone"
                className="mt-3 block font-display text-4xl md:text-5xl text-navy hover:text-cyan-brand transition-colors duration-500"
              >
                {LAB.phone}
              </a>
              <a
                href={LAB.phone2Href}
                data-testid="home-cta-phone2"
                className="mt-1 block font-display text-3xl md:text-4xl text-navy/80 hover:text-cyan-brand transition-colors duration-500"
              >
                {LAB.phone2}
              </a>
              <a
                href={LAB.emailHref}
                data-testid="home-cta-email"
                className="mt-2 block text-slate-500 hover:text-navy transition-colors"
              >
                {LAB.email}
              </a>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
